import { z } from 'zod';
import {
  CalendarDateSchema,
  InstantSchema,
  JobOrderNoSchema,
  KitIdSchema,
  OmittedIdentityIdSchema,
  OmittedIdentitySchema,
  OmittedMoneySchema,
  OriginSchema,
  RowVersionSchema,
  StageSchema,
} from './common.schema';
import { DeletionSchema, JobOrderArchiveSchema } from './jobOrder.schema';
import { ARCHIVE_STATUSES, SHIPPED_DAYS_SORT } from '../../src/domain/order/shipping';

export { ARCHIVE_STATUSES, SHIPPED_DAYS_SORT };

/**
 * The two things the delivered set says about a case: `shipped` until somebody
 * marks it received, `received` from then on. The `status` filter of
 * `GET /archive` and of its export. The request of 5 October 2026 (third),
 * section 11.
 */
export const ArchiveStatusSchema = z.enum(ARCHIVE_STATUSES);

/**
 * The three sets `GET /archive` serves. `delivered` is the archive as it always
 * was, and the default. `deleted` is the Deleted tab: cases deleted and waiting
 * to be purged, and cases with a deletion waiting for approval. `archived` is the
 * Archived tab, section 4 of the request of 24 September 2026: cases taken off
 * every list of work in hand without leaving the lab.
 */
export const ARCHIVE_SETS = ['delivered', 'deleted', 'archived'] as const;
export type ArchiveSet = (typeof ARCHIVE_SETS)[number];

/**
 * A row in the archive list.
 *
 * This is deliberately NOT a `JobOrder`. Section 5.6 of the backend
 * specification sorts the archive by `deliveredAt`, `product` and `units`, and
 * a job order carries none of those: it has a delivery date rather than a
 * delivered instant, and its product lives on the kits. The prototype's archive
 * table has the same nine columns as this schema, in the same order.
 *
 * The archive spans two kinds of case: an order dispatched from the live floor,
 * which still has kits and a case record behind it, and a delivered historical
 * order, which does not. `kitId` is how the two are told apart.
 */
export const ArchiveRowSchema = z.object({
  jobOrderNo: JobOrderNoSchema,

  // Section 6 item 1. Absent for a caller without seeIdentity.
  dentistName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  /**
   * The doctor's and the patient's records, sent only where the two names are.
   * The request of 6 October 2026 (second), items 4 and 5.
   */
  doctorId: OmittedIdentityIdSchema,
  patientId: OmittedIdentityIdSchema,

  product: z.string(),
  units: z.number().int().nonnegative(),
  /** Local, international or online. Section 1 of the request of 24 September 2026. */
  origin: OriginSchema,
  /**
   * The hand-over: when the case left the lab. Named deliveredAt from before a
   * case could be received, and deliberately never renamed, because
   * ARCHIVE_SORT_FIELD and the sales report both key off it and moving them to
   * the received date would move the lab's revenue by a day for every case.
   */
  deliveredAt: InstantSchema,
  /** When the customer had it. Null for a case nobody has marked received: the ones unaccounted for. */
  deliveredToCustomerAt: InstantSchema.nullable(),
  /**
   * How long the case has been in Shipped, in whole days on the lab's calendar:
   * from the day it left to the day the customer had it, or to today while
   * nobody has marked it received. Running while it is Shipped, standing still
   * once it is Received, never negative. The request of 5 October 2026 (third),
   * section 11: so a delayed delivery is easy to spot.
   */
  daysInShipped: z.number().int().nonnegative(),

  // Section 6 item 2. Absent without pay or admin.
  value: OmittedMoneySchema,

  carrier: z.string().nullable(),
  /** Typed by hand from the waybill at dispatch. Never generated. */
  trackingCode: z.string().nullable(),

  /** The kit to open the case record from, or null for a delivered historical order. */
  kitId: KitIdSchema.nullable(),

  /** The job order that remakes this case, once it has been sent back, or null. */
  redoneAs: JobOrderNoSchema.nullable(),

  /** The order's, sent as If-Match when the row is marked received from the list. */
  rowVersion: RowVersionSchema,
});

/**
 * The sort keys `GET /archive` accepts, with a leading `-` for descending.
 * Named as the backend specification names them, which is why `dentist` is not
 * `dentistName`: the sort key is a column, not a field path.
 *
 * `SHIPPED_DAYS_SORT` is accepted beside them and is not one of them: like the
 * three origin sorts it is a named order rather than a column, and a leading
 * minus changes nothing.
 */
export const ARCHIVE_SORT_KEYS = [
  'deliveredAt',
  'value',
  'jobOrderNo',
  'dentist',
  'product',
  'units',
  // Section 3 of the request of 13 September 2026, for the archive's Newest
  // first preset and its Patient column. `createdAt` is when the case was
  // TAKEN IN, which is not when it was delivered: a case opened in March and
  // delivered yesterday is newest by one and oldest by the other.
  'createdAt',
  'patient',
] as const;

export type ArchiveSortKey = (typeof ARCHIVE_SORT_KEYS)[number];

/**
 * Maps a sort key to the property it orders by.
 *
 * A string rather than `keyof ArchiveRow`: `createdAt` is not on the row and is
 * not going to be. When a case was taken in is not something the archive shows,
 * it is only something it can be ordered by, and widening every row in the
 * archive to carry a field nobody renders would be paying for the sort twice.
 */
export const ARCHIVE_SORT_FIELD: Record<ArchiveSortKey, string> = {
  deliveredAt: 'deliveredAt',
  value: 'value',
  jobOrderNo: 'jobOrderNo',
  dentist: 'dentistName',
  product: 'product',
  units: 'units',
  createdAt: 'createdAt',
  patient: 'patientName',
};

/**
 * A row of `GET /archive?set=deleted`: a case deleted, or waiting for a deletion
 * to be approved. Not an ArchiveRow, because such a case may never have been
 * delivered, and what the tab needs is who deleted it and when it goes.
 *
 * The rows waiting for approval come first, then the deleted ones, most recently
 * deleted first.
 */
export const DeletedCaseRowSchema = z.object({
  jobOrderNo: JobOrderNoSchema,

  // Absent for a caller without seeIdentity, as everywhere.
  dentistName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  // The records behind the two names, absent with them.
  doctorId: OmittedIdentityIdSchema,
  patientId: OmittedIdentityIdSchema,

  product: z.string(),
  /** The kit, or null for a delivered historical order with none. */
  kitId: KitIdSchema.nullable(),
  deletion: DeletionSchema,
  /** The order's, sent as If-Match when the row is approved, refused or restored. */
  rowVersion: RowVersionSchema,
});

/**
 * A row of `GET /archive?set=archived`: a case archived without leaving the lab.
 * Section 4 of the request of 24 September 2026. Not an ArchiveRow, which is a
 * delivered case and carries when it left and what it earned; this carries where
 * its tray stopped and who archived it, which is what taking it out again needs.
 *
 * The most recently archived first, or one origin's first under the origin sorts.
 */
export const ArchivedCaseRowSchema = z.object({
  jobOrderNo: JobOrderNoSchema,

  // Absent for a caller without seeIdentity, as everywhere.
  dentistName: OmittedIdentitySchema,
  patientName: OmittedIdentitySchema,
  // The records behind the two names, absent with them.
  doctorId: OmittedIdentityIdSchema,
  patientId: OmittedIdentityIdSchema,

  product: z.string(),
  units: z.number().int().nonnegative(),
  origin: OriginSchema,
  /** The bench its tray stopped at, and comes back to. Null for an order with no kits. */
  stage: StageSchema.nullable(),
  kitId: KitIdSchema.nullable(),
  deliveryDate: CalendarDateSchema,
  archive: JobOrderArchiveSchema,
  /** The order's. */
  rowVersion: RowVersionSchema,
});

export type ArchiveRow = z.infer<typeof ArchiveRowSchema>;
export type DeletedCaseRow = z.infer<typeof DeletedCaseRowSchema>;
export type ArchivedCaseRow = z.infer<typeof ArchivedCaseRowSchema>;
